import React, { useState, useRef, useEffect } from "react";
import { FaApple, FaHeartbeat, FaSearch, FaChevronDown, FaChevronUp, FaChartLine } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import CountryList from "../../CountryList";
import { appleApi } from "../../../services/api";

const COMPONENT_LABELS = {
  keyword_visibility: "Keyword Visibility",
  rating: "Rating",
  review_sentiment: "Review Sentiment",
  retention: "Retention",
  installs_trend: "Installs Trend",
  localization_completeness: "Localization Completeness",
};

const KeywordHealth = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("us");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [expandedComponent, setExpandedComponent] = useState(null);

  const searchTimers = useRef([]);
  const searchReqId = useRef(0);
  const searchAbortRef = useRef(null);

  useEffect(() => {
    const st = searchTimers.current;
    const sa = searchAbortRef.current;
    return () => {
      st.forEach(clearTimeout);
      if (sa) sa.abort();
    };
  }, []);

  const startTimedMessages = (setFn, bucketRef) => {
    bucketRef.current.forEach(clearTimeout);
    bucketRef.current = [];
    setFn("Fetching your results…");
    bucketRef.current.push(
      setTimeout(() => setFn("Still fetching… this may take a little while."), 10_000)
    );
    bucketRef.current.push(
      setTimeout(() => setFn("Working on it… analyzing many results can be slow."), 30_000)
    );
    bucketRef.current.push(
      setTimeout(() => setFn("Almost there… large datasets take longer than usual."), 60_000)
    );
  };

  const stopTimedMessages = (setFn, bucketRef) => {
    bucketRef.current.forEach(clearTimeout);
    bucketRef.current = [];
    setFn("");
  };

  const handleGet = async () => {
    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) {
      setError("Keyword is required.");
      return;
    }

    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    const myId = ++searchReqId.current;
    const countryCode = country || "us";

    setLoading(true);
    setError(null);
    setData(null);
    setExpandedComponent(null);
    startTimedMessages(setStatusMsg, searchTimers);

    try {
      const response = await appleApi.get("apple-keyword-health/", {
        params: { keyword: trimmedKeyword, country: countryCode },
        signal: controller.signal,
      });

      if (searchReqId.current === myId) {
        setData(response.data);
      }
    } catch (err) {
      const canceled =
        err?.code === "ERR_CANCELED" ||
        err?.name === "CanceledError" ||
        err?.message === "canceled";
      if (!canceled && searchReqId.current === myId) {
        setError("Failed to fetch keyword health. Please try again.");
      }
    } finally {
      setLoading(false);
      stopTimedMessages(setStatusMsg, searchTimers);
      if (searchAbortRef.current === controller) searchAbortRef.current = null;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 40) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return "bg-green-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-red-500";
  };

  const renderBreakdown = (breakdown) => {
    if (!breakdown || typeof breakdown !== "object") return null;
    return (
      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mt-2">
        {Object.entries(breakdown).map(([key, value]) => (
          <li key={key}>
            <span className="font-medium text-gray-700">
              {key.replace(/_/g, " ")}:
            </span>{" "}
            {typeof value === "number"
              ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : String(value)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header & Form */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaHeartbeat className="mr-3 text-blue-500" />
          Keyword Health
          <span className="ml-3 inline-flex items-center gap-1 text-sm font-normal text-gray-500 bg-gray-100 rounded-full px-3 py-1">
            <FaApple size={13} /> App Store
          </span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keyword <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter keyword"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleGet()}
              className="w-full bg-white border border-gray-300 rounded-md py-2.5 px-4 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>

          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Country
            </label>
            <CountryList onSelect={(code) => setCountry(code || "us")} />
          </div>

          <div className="md:col-span-2">
            <button
              onClick={handleGet}
              disabled={loading || !keyword.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2.5 px-4 rounded-md shadow transition duration-200 flex items-center justify-center"
            >
              {loading ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              ) : (
                <FaSearch className="mr-2" size={14} />
              )}
              {loading ? "Getting…" : "Get"}
            </button>
          </div>
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-600 font-medium">{error}</p>
        )}
      </div>

      {/* Loading */}
      {loading && !data && (
        <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 text-center">
          <Spinner animation="border" variant="primary" />
          {statusMsg && (
            <p className="mt-3 text-sm text-gray-500">{statusMsg}</p>
          )}
        </div>
      )}

      {/* Results */}
      {data && !loading && (
        <div className="space-y-6">
          {/* Overall Health Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-md p-6 border border-blue-100">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center ${getScoreBgColor(
                    data.overall_health
                  )} text-white flex-shrink-0`}
                >
                  <FaHeartbeat size={26} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Overall Keyword Health
                  </h3>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{data.keyword}</span>
                    {" · "}
                    <span className="uppercase">{data.country}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-baseline gap-2">
                <span
                  className={`text-5xl font-bold ${getScoreColor(
                    data.overall_health
                  )}`}
                >
                  {typeof data.overall_health === "number"
                    ? data.overall_health.toFixed(2)
                    : data.overall_health}
                </span>
                <span className="text-xl text-gray-500 font-medium">/ 100</span>
              </div>
            </div>
          </div>

          {/* Components Table */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FaChartLine className="mr-2 text-blue-600" />
                Health Components
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Click any row to see its detailed breakdown.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="px-6 py-3 font-semibold text-gray-700">
                      Component
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-700 text-right">
                      Score
                    </th>
                    <th className="px-6 py-3 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {data.components &&
                    Object.entries(data.components).map(([key, comp]) =>
                      comp &&
                      typeof comp === "object" &&
                      "score" in comp ? (
                        <React.Fragment key={key}>
                          <tr
                            onClick={() =>
                              setExpandedComponent(
                                expandedComponent === key ? null : key
                              )
                            }
                            className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                          >
                            <td className="px-6 py-4">
                              <span className="font-medium text-gray-800 block">
                                {COMPONENT_LABELS[key] ||
                                  key.replace(/_/g, " ")}
                              </span>
                              <span className="text-xs text-gray-400">
                                Click to see details
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span
                                className={`font-bold text-base ${getScoreColor(
                                  comp.score
                                )}`}
                              >
                                {typeof comp.score === "number"
                                  ? comp.score.toFixed(2)
                                  : comp.score}
                              </span>
                              <span className="text-gray-400 text-xs ml-1">
                                / 100
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-400">
                              {expandedComponent === key ? (
                                <FaChevronUp />
                              ) : (
                                <FaChevronDown />
                              )}
                            </td>
                          </tr>

                          {expandedComponent === key && (
                            <tr className="bg-blue-50 border-b border-blue-100">
                              <td colSpan={3} className="px-8 py-5">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-700">
                                      Score:
                                    </span>
                                    <span
                                      className={`text-sm font-bold ${getScoreColor(
                                        comp.score
                                      )}`}
                                    >
                                      {typeof comp.score === "number"
                                        ? comp.score.toFixed(2)
                                        : comp.score}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                      / 100
                                    </span>
                                  </div>

                                  {comp.breakdown &&
                                    renderBreakdown(comp.breakdown)}

                                  {key === "keyword_visibility" &&
                                    comp.tracked_keywords &&
                                    Array.isArray(comp.tracked_keywords) && (
                                      <div className="mt-3 pt-3 border-t border-blue-100">
                                        <p className="text-sm font-semibold text-gray-700 mb-2">
                                          Tracked Keywords
                                        </p>
                                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                          {comp.tracked_keywords.map(
                                            (tk, i) => (
                                              <li key={i}>
                                                <span className="font-medium">
                                                  {tk.keyword}
                                                </span>
                                                {tk.search_volume != null && (
                                                  <span className="text-gray-500">
                                                    {" "}
                                                    — Vol:{" "}
                                                    {Number(
                                                      tk.search_volume
                                                    ).toLocaleString(
                                                      undefined,
                                                      {
                                                        maximumFractionDigits: 0,
                                                      }
                                                    )}
                                                  </span>
                                                )}
                                                {tk.rank != null && (
                                                  <span className="text-gray-500">
                                                    {" "}
                                                    · Rank: {tk.rank}
                                                  </span>
                                                )}
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ) : null
                    )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordHealth;
